import React from 'react';
import {useSelector} from 'react-redux';
import {get} from 'lodash';

import AcceptByComponent from '/component/partial/report/AcceptByComponent/AcceptByComponent';
import {CandidateReportAcceptByIconComponent} from '/component/partial/report/CandidateReportAcceptByIconComponent/CandidateReportAcceptByIconComponent';
import CandidatesListHeader from '/component/partial/report/CandidatesListHeader/CandidatesListHeader';
import PeopleReportDataRenderer from '/component/partial/report/PeopleReportDataRenderer';
import ReferralDateAddedComponent from '/component/partial/report/ReferralDateAddedComponent';
import ReportDataProvider from '/component/partial/report/ReportDataProvider';
import {REFERRAL_PROFILE} from '/enum/popover.enum';
import {REPORT_ALL_REFERRALS} from '/enum/records.enum';
import {EVENT_NAMES, SCREEN_NAMES, USER_TYPES} from '/enum/tealium.enum';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {
  getReferralState,
  useCoachAnalyticsData,
} from '/util/referralAnalytics.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

import ReferralRecordExpiration from '../../../partial/report/ReferralRecordExpiration';

import reportPayload from './reportPayload';

import {useRecordsSorting} from '/context/RecordsSort/useRecordsSorting';

interface AllReferralsContentProps {
  loadingData: any;
  recordsShowRecord: any;
}

export const AllReferralsContent: React.FC<
  AllReferralsContentProps
> = props => {
  const {ff_accept_decline = ''}: any = useSelector(variablesFeatureFlag);
  const {isReadyToLoad, sortParam} = useRecordsSorting({
    recordsName: reportPayload.reportName,
  });
  const coachAnalyticsData = useCoachAnalyticsData();

  const recordsName = reportPayload.reportName;

  const expirationIdentifier = (receivedDate: string): boolean => {
    try {
      const dateUtil = getDateUtilDefaultInstance();
      const parsed = dateUtil.parseEasternDate(receivedDate);
      if (!parsed || isNaN(parsed.getTime())) {
        // fallback policy: treat invalid as expired
        return true;
      }
      return Date.now() > parsed.getTime();
    } catch (err) {
      // on error, treat as expired
      return true;
    }
  };

  const handleRecordPress = (reportData: any) => {
    // Track profile_click event
    const sendProfileClickEvent =
      getTealiumUtilDefaultInstance().generateTrackEvent({
        eventName: EVENT_NAMES.profileClick,
      });

    // Try multiple possible field combinations for user name
    const userFullName =
      reportData.fullName ||
      reportData.name ||
      `${reportData.firstName || ''} ${reportData.lastName || ''}`.trim() ||
      reportData.candidateName ||
      'Unknown User';

    const expirationTime = get(reportData, 'expirationTime', '');
    const isExpired = expirationIdentifier(expirationTime);
    const eventData = {
      state: getReferralState(isExpired),
      userFullName: userFullName,
      screenName: REPORT_ALL_REFERRALS,
      target_location: 'referral_profile',
      userType: USER_TYPES.candidateReferral,
      source: 'REFERRAL_PROFILE_NAVIGATION',
      ...coachAnalyticsData,
    };

    sendProfileClickEvent(eventData);

    // Navigate to referral profile
    props.recordsShowRecord({
      recordType: REFERRAL_PROFILE,
      recordId: reportData.prospectInfoId,
    });
  };

  if (!isReadyToLoad) {
    return null;
  }

  return (
    <ReportDataProvider
      {...reportPayload}
      dataRenderer={PeopleReportDataRenderer}
      hideAvatar={true}
      dataRendererProps={{
        listHeaderComponent: CandidatesListHeader,
        personNameComponent: ReferralRecordExpiration,
        personRoleComponent: ReferralDateAddedComponent,
        renderNameElementDirectly: true,
        renderRoleElementDirectly: true,
        AcceptByComponent:
          shouldReleaseFeature(ff_accept_decline) && AcceptByComponent,
        AcceptByIconComponent:
          shouldReleaseFeature(ff_accept_decline) &&
          CandidateReportAcceptByIconComponent,
        isCandidateReport: true,
        onRecordPress: handleRecordPress,
        alternateItemKey: 'prospectInfoId',
      }}
      reportName={recordsName}
      loadingData={props.loadingData}
      currentScreenName={SCREEN_NAMES.clientsToContact}
      reportParams={[{...sortParam.params}]}
    />
  );
};
